import type { HelpTopicPair } from "../types";

const topic: HelpTopicPair = {
  th: {
    id: "client-detail",
    title: "รายละเอียด OAuth client",
    summary:
      "หน้าจัดการแอปพลิเคชันหนึ่งตัว: แก้ไขการตั้งค่า กำหนดว่าใครเข้าสู่ระบบได้ ปรับหน้าตาหน้าเข้าสู่ระบบของแอป ดูข้อมูลสำหรับนักพัฒนา สร้าง secret ใหม่ และเปิด/ปิดใช้งานแอป",
    access:
      "เปิดดูได้เมื่อมีสิทธิ์ clients:read (owner, admin, auditor) การแก้ไขทุกอย่างและปุ่ม “สร้าง secret ใหม่” / “ปิดใช้งาน” ต้องมีสิทธิ์ clients:write (owner, admin) — ผู้ที่ไม่มีสิทธิ์จะเห็นฟอร์มเป็นแบบอ่านอย่างเดียวและไม่มีปุ่มบันทึก",
    shot: { file: "client-detail-settings.png", caption: "หน้ารายละเอียดแอป แท็บ “การตั้งค่า”" },
    sections: [
      {
        id: "header",
        heading: "ส่วนหัวของหน้า",
        body: [
          "ลิงก์ “กลับไปหน้า clients” ด้านบนพากลับไปที่รายการแอป ใต้ชื่อแอปมี Client ID (พร้อมปุ่มคัดลอก) ป้ายประเภท (Confidential / Public (SPA / มือถือ)) ป้าย “แอปภายใน” ถ้ามี และป้ายสถานะ “เปิดใช้งาน” / “ปิดใช้งาน”",
          "ด้านขวามีปุ่ม “สร้าง secret ใหม่” (เฉพาะแอปประเภท Confidential) และปุ่ม “ปิดใช้งาน” หรือ “เปิดใช้งาน” — แสดงเฉพาะผู้ที่มีสิทธิ์ clients:write",
          "ด้านล่างแบ่งเป็นสี่แท็บ: “การตั้งค่า”, “สิทธิ์เข้าถึง”, “แบรนด์” และ “การเชื่อมต่อ” แต่ละแท็บมีปุ่ม “บันทึก” ของตัวเอง — การเปลี่ยนแท็บโดยยังไม่กดบันทึกจะทำให้การแก้ไขในแท็บนั้นหายไป",
        ],
      },
      {
        id: "settings",
        heading: "แท็บ “การตั้งค่า”",
        body: [
          "ฟอร์มเดียวกับตอนลงทะเบียน (ยกเว้น “ประเภท” ซึ่งเปลี่ยนไม่ได้): ชื่อแอปพลิเคชัน, Redirect URIs, Post-logout redirect URIs, Scopes ที่อนุญาต, แอปภายใน, Back-channel logout URI, ทีมเจ้าของ และคำอธิบาย ความหมายและกฎของแต่ละช่องดูได้ที่หัวข้อ “OAuth clients” แก้ไขแล้วกด “บันทึก” ระบบจะแสดงข้อความ “อัปเดต client แล้ว”",
          "การล้างช่องที่ไม่บังคับ (เช่น ทีมเจ้าของ) แล้วบันทึก จะลบค่านั้นออก",
        ],
        fieldsTitle: "ผลของการแก้ไขต่อผู้ใช้",
        fields: [
          {
            name: "ชื่อแอปพลิเคชัน",
            description: "ผู้ใช้เห็นชื่อใหม่ในหน้าเข้าสู่ระบบครั้งถัดไป ไม่กระทบผู้ที่เข้าระบบอยู่",
          },
          {
            name: "Redirect URIs",
            description:
              "ถ้าลบหรือแก้ URI ที่แอปใช้อยู่ ผู้ใช้จะเข้าสู่ระบบแอปนั้นไม่ได้ทันที (ระบบปฏิเสธเพราะที่อยู่ไม่ตรง) ต้องมีอย่างน้อยหนึ่งรายการเสมอ ประสานกับทีมพัฒนาก่อนแก้",
          },
          {
            name: "Scopes ที่อนุญาต",
            description:
              "การเอา scope ออกมีผลกับการเข้าสู่ระบบครั้งถัดไป แอปที่ยังขอ scope นั้นอยู่อาจเข้าสู่ระบบไม่สำเร็จ",
          },
          {
            name: "แอปภายใน",
            description: "เปิด = ผู้ใช้ไม่ต้องเห็นหน้าขอความยินยอมอีก ปิด = ผู้ใช้ต้องกดยินยอมหนึ่งครั้ง",
          },
        ],
        warnings: [
          "สำหรับ client ของคอนโซลผู้ดูแลเอง Redirect URIs และ Scopes จัดการโดยแพลตฟอร์ม การบันทึกค่าที่เปลี่ยนไปจะถูกปฏิเสธ",
        ],
      },
      {
        id: "access",
        heading: "แท็บ “สิทธิ์เข้าถึง” — ใครเข้าสู่ระบบแอปนี้ได้",
        shot: {
          file: "client-detail-access.png",
          caption: "แท็บ “สิทธิ์เข้าถึง” แบบจำกัดเฉพาะประเภทผู้ใช้และกลุ่ม",
        },
        body: [
          "ระบบตรวจสิทธิ์นี้ทุกครั้งก่อนส่งผู้ใช้กลับไปที่แอป ผู้ที่ไม่มีสิทธิ์จะไม่เข้าแอปเลย แต่จะเห็นหน้า “ไม่มีสิทธิ์เข้าแอปนี้” ของระบบกลาง พร้อมตัวเลือกให้เข้าสู่ระบบด้วยบัญชีอื่น",
          "หัวข้อ “ใครเข้าสู่ระบบได้บ้าง” มีสองตัวเลือก:",
          "• “ทุกคน” — บัญชีที่ใช้งานอยู่ทุกบัญชีเข้าสู่ระบบได้ รวมถึงลูกค้าที่สมัครเอง เหมาะกับแอปสำหรับลูกค้าทั่วไป",
          "• “เฉพาะประเภทผู้ใช้และกลุ่มที่เลือก” — เข้าได้เฉพาะบัญชีที่เป็นประเภทผู้ใช้ที่เลือก หรือเป็นสมาชิกของกลุ่มที่เลือก (ตรงอย่างใดอย่างหนึ่งก็พอ) เมื่อเลือกตัวเลือกนี้จะมีช่อง “ประเภทผู้ใช้” และ “กลุ่ม” ให้เลือกได้หลายรายการ ต้องเลือกอย่างน้อยหนึ่งรายการ",
          "ช่อง “อนุญาตให้สมัครเป็น” — ให้ผู้เข้าชมที่ยังไม่มีบัญชีสมัครสมาชิกเองผ่านแอปนี้ได้ โดยจะได้บัญชีประเภทที่เลือก (เช่น customer) รายการแสดงเฉพาะประเภทที่เปิด self sign-up ไว้ในหน้าประเภทผู้ใช้ เลือก “ไม่เปิดให้สมัคร” ถ้าไม่ต้องการให้สมัครเอง",
          "แอปใหม่จะอนุญาตเฉพาะ employee เสมอ เพื่อไม่ให้ลูกค้าหรือคู่ค้าเข้าแอปภายในโดยไม่ตั้งใจ ส่วนแอปที่ลงทะเบียนไว้ก่อนมีฟีเจอร์นี้จะเป็น “ทุกคน”",
        ],
        fieldsTitle: "ข้อความแจ้งเตือนที่อาจเห็น (ปุ่มบันทึกจะกดไม่ได้จนกว่าจะแก้)",
        fields: [
          {
            name: "เลือกประเภทผู้ใช้หรือกลุ่มอย่างน้อยหนึ่งรายการ",
            description: "เลือกแบบจำกัดแต่ยังไม่ได้เลือกอะไรเลย",
          },
          {
            name: "ประเภทผู้ใช้นี้ไม่อนุญาตให้สมัครเอง (self sign-up)",
            description:
              "ประเภทที่ตั้งให้สมัครถูกปิด self sign-up ไปแล้ว — เปิดในหน้าประเภทผู้ใช้ หรือเลือกประเภทอื่น",
          },
          {
            name: "ประเภทที่ใช้สมัครต้องอยู่ในประเภทผู้ใช้ที่เลือกด้วย",
            description:
              "ในโหมดจำกัด ถ้าเปิดให้สมัครเป็น customer ต้องเลือก customer ในช่อง “ประเภทผู้ใช้” ด้วย มิฉะนั้นคนที่สมัครแล้วจะเข้าแอปไม่ได้",
          },
        ],
        steps: [
          "เปิดแท็บ “สิทธิ์เข้าถึง”",
          "เลือก “ทุกคน” หรือ “เฉพาะประเภทผู้ใช้และกลุ่มที่เลือก”",
          "ถ้าเลือกแบบจำกัด เลือกประเภทผู้ใช้และ/หรือกลุ่ม (พิมพ์ค้นหาในรายการได้)",
          "เลือก “อนุญาตให้สมัครเป็น” ถ้าต้องการให้สมัครเองได้",
          "กด “บันทึก”",
        ],
        warnings: [
          "เมื่อจำกัดสิทธิ์ให้แคบลง token ของผู้ใช้ที่ไม่มีสิทธิ์แล้วจะถูกเพิกถอนทันที และถูกออกจากระบบของแอปนี้ — ตรวจรายชื่อให้ดีก่อนบันทึก",
          "การเพิ่มสิทธิ์ (ให้คนเข้าได้มากขึ้น) ไม่ทำให้ใครถูกออกจากระบบ",
          "สำหรับคอนโซลผู้ดูแล แท็บนี้เป็นแบบอ่านอย่างเดียว พร้อมข้อความ “สิทธิ์เข้าคอนโซลผู้ดูแลจัดการโดยแพลตฟอร์ม: เฉพาะบัญชีที่มีบทบาทคอนโซลเท่านั้นที่เข้าได้”",
        ],
        tips: [
          "ตัวอย่าง: ระบบหลังบ้านแชต → เฉพาะกลุ่ม support · พอร์ทัลคู่ค้า → ประเภท partner · แอปลูกค้า → “ทุกคน” + อนุญาตให้สมัครเป็น customer",
          "บัญชีระบบ root เข้าได้เฉพาะคอนโซลผู้ดูแล ไม่ว่าแอปอื่นจะตั้งสิทธิ์ไว้อย่างไร",
          "ดูได้ว่าผู้ใช้แต่ละคนเข้าแอปไหนได้บ้าง ที่หน้าผู้ใช้ → แท็บสิทธิ์เข้าถึง",
        ],
      },
      {
        id: "branding",
        heading: "แท็บ “แบรนด์” — หน้าตาหน้าเข้าสู่ระบบของแอปนี้",
        shot: { file: "client-detail-branding.png", caption: "แท็บ “แบรนด์” พร้อมตัวอย่างทางขวา" },
        body: [
          "กำหนดโลโก้ สี และข้อความบนหน้าเข้าสู่ระบบ เฉพาะเมื่อผู้ใช้มาจากแอปนี้ ช่องที่เว้นว่างจะใช้ค่าจาก การตั้งค่า → แบรนด์ (ค่าส่วนกลาง) ทางขวามี “ตัวอย่าง” ที่อัปเดตทันทีขณะแก้ไข",
          "ขณะผู้ใช้เข้าสู่ระบบผ่านแอปนี้ หน้าเข้าสู่ระบบจะแสดงชื่อแอปข้างโลโก้ด้วย",
        ],
        fieldsTitle: "ช่องในแท็บแบรนด์",
        fields: [
          {
            name: "ใช้สีเฉพาะของแอปนี้",
            description:
              "ติ๊กเพื่อตั้งสีหลักของแอปนี้ แล้วเลือกสีจากตัวเลือกสีหรือพิมพ์รหัสสีแบบ #rrggbb (เช่น #f97316) ถ้าไม่ติ๊กจะใช้สีส่วนกลาง ระบบปรับสีตัวอักษรบนปุ่มให้อ่านง่ายอัตโนมัติ",
          },
          {
            name: "URL โลโก้",
            description:
              "ที่อยู่รูปภาพแบบ HTTPS แสดงแทนโลโก้ของระบบ เช่น https://chat.example.com/logo.svg (ไม่เกิน 2000 ตัวอักษร) เว้นว่าง = ใช้โลโก้ส่วนกลาง แนะนำ SVG หรือ PNG พื้นโปร่งใส",
          },
          {
            name: "หัวข้อ (ไทย) / หัวข้อ (อังกฤษ)",
            description:
              "หัวข้อตัวใหญ่ฝั่งซ้ายของหน้าเข้าสู่ระบบ แยกตามภาษาที่ผู้ใช้เลือก ไม่เกิน 120 ตัวอักษร กด Enter เพื่อขึ้นบรรทัดใหม่ เว้นว่าง = ใช้ข้อความส่วนกลาง (แสดงเป็นตัวจางในช่อง)",
          },
          {
            name: "ข้อความ (ไทย) / ข้อความ (อังกฤษ)",
            description: "ข้อความใต้หัวข้อ ไม่เกิน 500 ตัวอักษร เว้นว่าง = ใช้ข้อความส่วนกลาง",
          },
          {
            name: "เปิดหน้าเข้าสู่ระบบ",
            description: "ลิงก์เปิดหน้าเข้าสู่ระบบจริงของแอปนี้ในแท็บใหม่ เพื่อตรวจผลหลังบันทึก",
          },
        ],
        tips: [
          "ล้างทุกช่องแล้วบันทึก = กลับไปใช้แบรนด์ส่วนกลางทั้งหมด",
          "หน้าที่เปิดจากลิงก์ในอีเมล (รีเซ็ตรหัสผ่าน ยืนยันอีเมล) และหน้าบัญชีของฉัน ใช้แบรนด์ส่วนกลางเสมอ",
          "การเปลี่ยนแบรนด์ไม่ทำให้ใครถูกออกจากระบบ",
        ],
      },
      {
        id: "integration",
        heading: "แท็บ “การเชื่อมต่อ” — ข้อมูลสำหรับนักพัฒนา",
        shot: { file: "client-detail-integration.png", caption: "แท็บ “การเชื่อมต่อ” พร้อมตัวอย่างโค้ด" },
        body: [
          "รวบรวมข้อมูลที่ทีมพัฒนาต้องใช้ แต่ละบรรทัดมีปุ่มคัดลอก: Issuer (ที่อยู่ของระบบเข้าสู่ระบบกลาง), Discovery URL (ที่อยู่ที่ไลบรารีใช้ค้นหาการตั้งค่าทั้งหมดอัตโนมัติ) และ Client ID",
          "ส่วน “ตัวอย่างโค้ด” มีปุ่มเลือกภาษา Next.js, PHP, Python, Go และ Java (Spring) โค้ดใส่ Client ID, Redirect URI แรกของแอป และ scopes ของแอปให้แล้ว กดปุ่มคัดลอกมุมขวาบนของกล่องโค้ดเพื่อส่งต่อให้นักพัฒนา",
          "โค้ดตัวอย่างไม่มี secret จริง แต่จะอ่านจากตัวแปรสภาพแวดล้อม OIDC_CLIENT_SECRET ซึ่งทีมพัฒนาต้องตั้งเองจาก secret ที่ได้ตอนลงทะเบียน",
        ],
      },
      {
        id: "rotate",
        heading: "สร้าง secret ใหม่ (Rotate secret)",
        shot: { file: "client-detail-rotate.png", caption: "หน้าต่างยืนยันการสร้าง secret ใหม่" },
        body: [
          "ใช้เมื่อ secret เดิมหาย รั่วไหล หรือครบรอบการเปลี่ยนตามนโยบายความปลอดภัย มีเฉพาะแอปประเภท Confidential",
        ],
        steps: [
          "แจ้งทีมพัฒนาล่วงหน้าให้พร้อมเปลี่ยน secret ในแอป",
          "กด “สร้าง secret ใหม่” ที่ส่วนหัวของหน้า",
          "อ่านข้อความ “secret เดิมจะใช้ไม่ได้ทันที ต้องการดำเนินการต่อหรือไม่” แล้วกด “สร้าง secret ใหม่” ในหน้าต่างยืนยัน",
          "หน้าต่าง Client secret จะแสดง secret ใหม่ครั้งเดียว — คัดลอก แล้วกด “ฉันเก็บ secret แล้ว”",
          "ให้ทีมพัฒนาใส่ secret ใหม่ในแอปโดยเร็วที่สุด",
        ],
        warnings: [
          "secret เดิมใช้ไม่ได้ทันทีที่ยืนยัน แอปจะเข้าสู่ระบบผู้ใช้ไม่ได้จนกว่าจะใส่ secret ใหม่ — เลือกช่วงเวลาที่กระทบผู้ใช้น้อย",
          "secret ใหม่แสดงครั้งเดียวเท่านั้น",
        ],
      },
      {
        id: "disable",
        heading: "ปิดใช้งาน / เปิดใช้งานแอป",
        body: [
          "ปุ่ม “ปิดใช้งาน” หยุดแอปทันทีโดยไม่ลบข้อมูล: ไม่มีใครเข้าสู่ระบบผ่านแอปนี้ได้ และ token ที่แอปถืออยู่ทั้งหมดถูกเพิกถอน ผู้ใช้จึงถูกออกจากระบบของแอปนี้ (ต้องเข้าสู่ระบบใหม่เมื่อแอปกลับมาเปิด) ป้ายสถานะจะเปลี่ยนเป็น “ปิดใช้งาน”",
          "ปุ่ม “เปิดใช้งาน” (แสดงแทนเมื่อแอปถูกปิดอยู่) ทำให้แอปกลับมาใช้งานได้ด้วยการตั้งค่าเดิมทั้งหมด ผู้ใช้ต้องเข้าสู่ระบบใหม่",
          "คอนโซลนี้ไม่มีปุ่มลบแอปพลิเคชัน ให้ใช้ “ปิดใช้งาน” แทน ข้อมูลและประวัติของแอปจะยังอยู่ และเปิดกลับได้เสมอ",
        ],
        warnings: [
          "การปิดใช้งานมีผลกับผู้ใช้ทุกคนของแอปนั้นทันที (ไม่มีหน้าต่างยืนยัน) — แจ้งเจ้าของแอปก่อน",
          "คอนโซลผู้ดูแลปิดใช้งานไม่ได้",
        ],
        tips: [
          "ทุกการเปลี่ยนแปลงในหน้านี้ถูกบันทึกในบันทึกการใช้งาน (เช่น client.updated, client.secret_rotated, client.disabled)",
        ],
      },
    ],
    related: ["oauth-clients", "branding", "groups", "user-types", "audit-logs"],
  },
  en: {
    id: "client-detail",
    title: "OAuth client details",
    summary:
      "Manage one application: edit its settings, decide who may sign in, adjust its sign-in page look, get the developer details, rotate its secret, and enable or disable it.",
    access:
      "Viewing needs clients:read (owner, admin, auditor). Every change and the “Rotate secret” / “Disable” buttons need clients:write (owner, admin) — without it the forms are read-only and have no Save button.",
    shot: { file: "client-detail-settings.png", caption: "The application detail page, “Settings” tab" },
    sections: [
      {
        id: "header",
        heading: "The page header",
        body: [
          "The “Back to clients” link returns to the list. Under the name are the Client ID (with a copy button), the type badge (Confidential / Public (SPA / mobile)), the “First-party” badge if set, and the “Enabled” / “Disabled” status badge.",
          "On the right are “Rotate secret” (Confidential clients only) and “Disable” or “Enable” — shown only with clients:write.",
          "Below are four tabs: “Settings”, “Access”, “Branding” and “Integration”. Each tab has its own “Save” button — switching tabs without saving discards the changes in that tab.",
        ],
      },
      {
        id: "settings",
        heading: "The “Settings” tab",
        body: [
          "The same form as at registration (except “Type”, which cannot change): Application name, Redirect URIs, Post-logout redirect URIs, Allowed scopes, First-party, Back-channel logout URI, Owner team and Description. See “OAuth clients” for the meaning and rules of each field. Edit, click “Save”, and “Client updated” confirms it.",
          "Clearing an optional field (e.g. Owner team) and saving removes that value.",
        ],
        fieldsTitle: "What a change does to users",
        fields: [
          {
            name: "Application name",
            description: "Users see the new name on their next sign-in; signed-in users are not affected.",
          },
          {
            name: "Redirect URIs",
            description:
              "Removing or changing a URI the application uses makes sign-in to it fail immediately (the address no longer matches). At least one is always required. Coordinate with the developers first.",
          },
          {
            name: "Allowed scopes",
            description:
              "Removing a scope applies from the next sign-in; an application that still asks for it may fail to sign users in.",
          },
          {
            name: "First-party",
            description: "On = users no longer see the consent screen. Off = users must consent once.",
          },
        ],
        warnings: [
          "For the admin console's own client, the Redirect URIs and Scopes are managed by the platform; saving changed values is refused.",
        ],
      },
      {
        id: "access",
        heading: "The “Access” tab — who may sign in",
        shot: {
          file: "client-detail-access.png",
          caption: "The “Access” tab restricted to selected user types and groups",
        },
        body: [
          "The platform checks this rule every time before sending a user back to the application. People who are not allowed never reach the application; they see the platform's “no access to this application” page, which offers to sign in with another account.",
          "“Who can sign in” has two options:",
          "• “Everyone” — any active account can sign in, including customers who sign up themselves. Suits customer-facing applications.",
          "• “Only selected user types and groups” — accounts of a listed user type, or members of a listed group (either is enough), can sign in. This shows the “User types” and “Groups” multi-select fields; select at least one entry.",
          "“Allow sign-up as” lets visitors without an account register through this application; they get an account of the chosen type (e.g. customer). Only types with self sign-up enabled (on the User types page) are listed. Choose “No sign-up” to prevent self-registration.",
          "New applications always start as employees only, so customers or partners cannot reach internal applications by accident. Applications registered before this feature existed are “Everyone”.",
        ],
        fieldsTitle: "Messages you may see (Save stays disabled until fixed)",
        fields: [
          {
            name: "Select at least one user type or group.",
            description: "Restricted mode with nothing selected.",
          },
          {
            name: "This user type does not allow self sign-up.",
            description:
              "The sign-up type has self sign-up turned off — turn it on under User types, or choose another type.",
          },
          {
            name: "The sign-up type must also be one of the selected user types.",
            description:
              "In restricted mode, signing up as customer requires customer in “User types” too — otherwise new sign-ups could not sign in.",
          },
        ],
        steps: [
          "Open the “Access” tab.",
          "Choose “Everyone” or “Only selected user types and groups”.",
          "For restricted access, pick user types and/or groups (you can type to search).",
          "Choose “Allow sign-up as” if visitors may register.",
          "Click “Save”.",
        ],
        warnings: [
          "Tightening access immediately revokes the tokens of users who are no longer allowed; they are signed out of this application. Check the selection before saving.",
          "Widening access (letting more people in) signs nobody out.",
          "For the admin console this tab is read-only, with the note “Access to the admin console is managed by the platform: only accounts with a console role can sign in.”",
        ],
        tips: [
          "Examples: chat back office → group support only · partner portal → type partner · customer app → “Everyone” + allow sign-up as customer.",
          "The root system account can only use the admin console, whatever another application's rule says.",
          "To see which applications one person can use, open the user → Access tab.",
        ],
      },
      {
        id: "branding",
        heading: "The “Branding” tab — this application's sign-in look",
        shot: {
          file: "client-detail-branding.png",
          caption: "The “Branding” tab with the live preview on the right",
        },
        body: [
          "Sets the logo, color and text of the sign-in pages only when people arrive from this application. Empty fields use Settings → Branding (the global look). The “Preview” on the right updates as you type.",
          "While someone signs in through this application, the sign-in page also shows the application's name next to the logo.",
        ],
        fieldsTitle: "Fields on the Branding tab",
        fields: [
          {
            name: "Use a color for this application",
            description:
              "Tick to give the application its own primary color, then pick it or type a #rrggbb code (e.g. #f97316). Unticked = the global color. Button text colors are adjusted automatically for readability.",
          },
          {
            name: "Logo URL",
            description:
              "An HTTPS image shown instead of the product logo, e.g. https://chat.example.com/logo.svg (max 2000 characters). Empty = the global logo. SVG or transparent PNG works best.",
          },
          {
            name: "Headline (Thai) / Headline (English)",
            description:
              "The large headline on the left of the sign-in pages, per UI language. Max 120 characters; press Enter for a line break. Empty = the global text (shown greyed out in the field).",
          },
          {
            name: "Text (Thai) / Text (English)",
            description: "The text under the headline. Max 500 characters. Empty = the global text.",
          },
          {
            name: "Open the sign-in page",
            description:
              "Opens this application's real sign-in page in a new tab to check the result after saving.",
          },
        ],
        tips: [
          "Clear every field and save to return to the global branding.",
          "Pages opened from email links (password reset, email verification) and the My account pages always use the global branding.",
          "Branding changes sign nobody out.",
        ],
      },
      {
        id: "integration",
        heading: "The “Integration” tab — details for developers",
        shot: { file: "client-detail-integration.png", caption: "The “Integration” tab with code snippets" },
        body: [
          "Everything developers need, each with a copy button: Issuer (the central sign-in address), Discovery URL (where libraries look up all settings automatically) and Client ID.",
          "“Code snippets” has buttons for Next.js, PHP, Python, Go and Java (Spring). The code already contains the Client ID, the application's first Redirect URI and its scopes. Use the copy button at the top right of the code box to pass it on.",
          "The snippets never contain the real secret; they read it from the OIDC_CLIENT_SECRET environment variable, which developers set from the secret they received at registration.",
        ],
      },
      {
        id: "rotate",
        heading: "Rotate secret",
        shot: { file: "client-detail-rotate.png", caption: "Confirming “Rotate secret”" },
        body: [
          "Use it when the secret was lost or leaked, or when your security policy requires periodic changes. Confidential clients only.",
        ],
        steps: [
          "Tell the developers in advance so they are ready to update the application.",
          "Click “Rotate secret” in the page header.",
          "Read “Rotating immediately invalidates the current secret. Continue?” and click “Rotate secret” in the confirmation dialog.",
          "The Client secret dialog shows the new secret once — copy it, then click “I have stored the secret”.",
          "Have the developers put the new secret into the application as soon as possible.",
        ],
        warnings: [
          "The old secret stops working the moment you confirm; the application cannot sign anyone in until it has the new one — pick a quiet time.",
          "The new secret is shown only once.",
        ],
      },
      {
        id: "disable",
        heading: "Disable / enable the application",
        body: [
          "“Disable” stops the application immediately without deleting anything: nobody can sign in through it, and all its tokens are revoked, so users are signed out of it (they sign in again once it is re-enabled). The status badge changes to “Disabled”.",
          "“Enable” (shown instead while disabled) brings it back with all its settings; users must sign in again.",
          "The console has no delete button for applications — use “Disable” instead. The application's data and history stay, and it can always be re-enabled.",
        ],
        warnings: [
          "Disabling affects every user of the application at once, with no confirmation dialog — tell the application owner first.",
          "The admin console itself cannot be disabled.",
        ],
        tips: [
          "Every change on this page is recorded in the audit log (e.g. client.updated, client.secret_rotated, client.disabled).",
        ],
      },
    ],
    related: ["oauth-clients", "branding", "groups", "user-types", "audit-logs"],
  },
};

export default topic;
